import React, { useState, useEffect } from "react";
import { useCurrentEditor } from "@tiptap/react";
import styles from "./MenuBar.module.scss";
import ImageUpload from "./ImageUpload";
import ImageGroup from './ImageGroup';

const MenuGroup = ({ label, children, isExpanded, onToggle }) => {
  return (
    <div className={styles.menuGroup}>
      <button
        className={`${styles.menuGroupLabel} ${
          isExpanded ? styles.active : ""
        }`}
        onClick={onToggle}
      >
        {label}
      </button>
      <div className={styles.menuGroupContent}>{children}</div>
    </div>
  );
};

const MenuBar = () => {
  const { editor } = useCurrentEditor();
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const editorControls = document.querySelector(`.${styles.editorControls}`);
      if (editorControls) {
        const rect = editorControls.getBoundingClientRect();
        setIsSticky(rect.top <= 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!editor) {
    return null;
  }

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => {
      const newGroups = new Set(prev);
      if (newGroups.has(groupName)) {
        newGroups.delete(groupName);
      } else {
        newGroups.add(groupName);
      }
      return newGroups;
    });
  };

  const toggleHeadingStyle = (level, style) => {
    editor.chain().focus().toggleHeading({ level }).run();
    const node = editor.state.selection.$head.parent;
    if (node.type.name === "heading" && node.attrs.level === level) {
      if (level === 2) {
        const currentStyle = node.attrs.style || "";
        const newStyle = currentStyle === style ? "" : style;
        editor.commands.updateAttributes("heading", { style: newStyle });
      } else {
        const currentStyle = node.attrs.style || "";
        const newStyle = currentStyle === style ? "" : style;
        editor.commands.updateAttributes("heading", { style: newStyle });
      }
    }
  };

  const toggleHeadingBorder = () => {
    const node = editor.state.selection.$head.parent;
    if (node.type.name === "heading") {
      const currentStyle = node.attrs.style || "";
      const newStyle = currentStyle === "no-border" ? "" : "no-border";
      editor.commands.updateAttributes("heading", { style: newStyle });
    }
  };

  return (
    <div className={`${styles.editorControls} ${isSticky ? styles.sticky : ''}`}>
      <div className={styles.menuBar}>
        {/* Format */}
        <MenuGroup
          label="Format"
          isExpanded={expandedGroups.has("format")}
          onToggle={() => toggleGroup("format")}
        >
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive("bold") ? styles.active : ""}
          >
            Bold
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive("italic") ? styles.active : ""}
          >
            Italic
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive("underline") ? styles.active : ""}
          >
            Underline
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive("strike") ? styles.active : ""}
          >
            Strike
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            className={editor.isActive("highlight") ? styles.active : ""}
          >
            Highlight
          </button>
          <button
            onClick={toggleHeadingBorder}
            className={
              editor.isActive("heading", { level: 2 }) && 
              !editor.isActive("heading", { style: "no-border" }) 
                ? styles.active 
                : ""
            }
          >
            Toggle Heading Border
          </button>
          <button
            onClick={() => {
              const url = window.prompt("Enter URL");
              if (url) {
                editor
                  .chain()
                  .focus()
                  .extendMarkRange("link")
                  .setLink({ href: url })
                  .run();
              }
            }}
            className={editor.isActive("link") ? styles.active : ""}
          >
            Link
          </button>
          <button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editor.isActive("link")}
          >
            Unlink
          </button>
        </MenuGroup>

        {/* Heading */}
        <MenuGroup
          label="Headings"
          isExpanded={expandedGroups.has("headings")}
          onToggle={() => toggleGroup("headings")}
        >
          <button
            onClick={() => toggleHeadingStyle(1)}
            className={editor.isActive("heading", { level: 1 }) ? styles.active : ""}
          >
            H1
          </button>
          <button
            onClick={() => toggleHeadingStyle(2)}
            className={editor.isActive("heading", { level: 2 }) ? styles.active : ""}
          >
            H2
          </button>
          <button
            onClick={() => toggleHeadingStyle(3)}
            className={editor.isActive("heading", { level: 3 }) ? styles.active : ""}
          >
            H3
          </button>
          <button
            onClick={() => toggleHeadingStyle(4)}
            className={editor.isActive("heading", { level: 4 }) ? styles.active : ""}
          >
            H4
          </button>
          <button
            onClick={() => toggleHeadingStyle(5)}
            className={editor.isActive("heading", { level: 5 }) ? styles.active : ""}
          >
            H5
          </button>
          <button
            onClick={() => toggleHeadingStyle(6)}
            className={editor.isActive("heading", { level: 6 }) ? styles.active : ""}
          >
            H6
          </button>
          <button
            onClick={() => editor.chain().focus().setParagraph().run()}
            className={editor.isActive("paragraph") ? styles.active : ""}
          >
            Paragraph
          </button>
        </MenuGroup>

        {/* Lists */}
        <MenuGroup
          label="Lists"
          isExpanded={expandedGroups.has("lists")}
          onToggle={() => toggleGroup("lists")}
        >
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive("bulletList") ? styles.active : ""}
          >
            Bullet List
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive("orderedList") ? styles.active : ""}
          >
            Numbered List
          </button>
        </MenuGroup>

        {/* Blocks */}
        <MenuGroup
          label="Blocks"
          isExpanded={expandedGroups.has("blocks")}
          onToggle={() => toggleGroup("blocks")}
        >
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive("blockquote") ? styles.active : ""}
          >
            Blockquote
          </button>
          <button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            Horizontal Rule
          </button>
          <button
            onClick={() => editor.chain().focus().setCodeBlock().run()}
            className={editor.isActive("codeBlock") ? styles.active : ""}
          >
            Code Block
          </button>
        </MenuGroup>

        {/* Alignment */}
        <MenuGroup
          label="Align"
          isExpanded={expandedGroups.has("align")}
          onToggle={() => toggleGroup("align")}
        >
          <button
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            className={
              editor.isActive({ textAlign: "left" }) ? styles.active : ""
            }
          >
            Left
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            className={
              editor.isActive({ textAlign: "center" }) ? styles.active : ""
            }
          >
            Center
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            className={
              editor.isActive({ textAlign: "right" }) ? styles.active : ""
            }
          >
            Right
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
            className={
              editor.isActive({ textAlign: "justify" }) ? styles.active : ""
            }
          >
            Justify
          </button>
        </MenuGroup>

        {/* Table */}
        <MenuGroup
          label="Table"
          isExpanded={expandedGroups.has("table")}
          onToggle={() => toggleGroup("table")}
        >
          <button
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            Insert Table
          </button>
          <button
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            disabled={!editor.can().addColumnBefore()}
          >
            Add Column Before
          </button>
          <button
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            disabled={!editor.can().addColumnAfter()}
          >
            Add Column After
          </button>
          <button
            onClick={() => editor.chain().focus().deleteColumn().run()}
            disabled={!editor.can().deleteColumn()}
          >
            Delete Column
          </button>
          <button
            onClick={() => editor.chain().focus().addRowBefore().run()}
            disabled={!editor.can().addRowBefore()}
          >
            Add Row Before
          </button>
          <button
            onClick={() => editor.chain().focus().addRowAfter().run()}
            disabled={!editor.can().addRowAfter()}
          >
            Add Row After
          </button>
          <button
            onClick={() => editor.chain().focus().deleteRow().run()}
            disabled={!editor.can().deleteRow()}
          >
            Delete Row
          </button>
          <button
            onClick={() => editor.chain().focus().deleteTable().run()}
            disabled={!editor.can().deleteTable()}
          >
            Delete Table
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
            disabled={!editor.can().toggleHeaderColumn()}
          >
            Toggle Header Column
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
            disabled={!editor.can().toggleHeaderRow()}
          >
            Toggle Header Row
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeaderCell().run()}
            disabled={!editor.can().toggleHeaderCell()}
          >
            Toggle Header Cell
          </button>
          <button
            onClick={() => editor.chain().focus().mergeCells().run()}
            disabled={!editor.can().mergeCells()}
          >
            Merge Cells
          </button>
          <button
            onClick={() => editor.chain().focus().splitCell().run()}
            disabled={!editor.can().splitCell()}
          >
            Split Cell
          </button>
        </MenuGroup>

        {/* Media */}
        <MenuGroup
          label="Media"
          isExpanded={expandedGroups.has("media")}
          onToggle={() => toggleGroup("media")}
        >
          <ImageUpload />
          <ImageGroup />
          {/* <button
            onClick={() => {
              const url = window.prompt("Enter image URL");
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }}
          >
            Insert Image URL
          </button> */}
          <button
            onClick={() => editor.chain().focus().deleteSelection().run()}
            disabled={!editor.isActive("image")}
          >
            Remove Image
          </button>
        </MenuGroup>

        {/* Colors */}
        <MenuGroup
          label="Colors"
          isExpanded={expandedGroups.has("colors")}
          onToggle={() => toggleGroup("colors")}
        >
          <button
            onClick={() => editor.chain().focus().setColor("#07587f").run()}
            className={`${styles.colorButton} blue ${
              editor.isActive("textStyle", { color: "#07587f" }) ? styles.active : ""
            }`}
          >
            Blue
          </button>
          <button
            onClick={() => editor.chain().focus().setColor("#09293B").run()}
            className={`${styles.colorButton} dark ${
              editor.isActive("textStyle", { color: "#09293B" }) ? styles.active : ""
            }`}
          >
            Dark
          </button>
          <button
            onClick={() =>
              editor.chain().focus().setColor("rgba(7, 88, 127, 0.1)").run()
            }
            className={`${styles.colorButton} light ${
              editor.isActive("textStyle", { color: "rgba(7, 88, 127, 0.1)" })
                ? styles.active
                : ""
            }`}
          >
            Light
          </button>
          <button
            onClick={() => editor.chain().focus().setColor("#FFFFFF").run()}
            className={`${styles.colorButton} white ${
              editor.isActive("textStyle", { color: "#FFFFFF" }) ? styles.active : ""
            }`}
          >
            White
          </button>
        </MenuGroup>

        <div className={styles.undoRedo}>
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().chain().focus().undo().run()}
          >
            Undo
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().chain().focus().redo().run()}
          >
            Redo
          </button>
        </div>
      </div>

      {expandedGroups.size > 0 && (
        <div className={styles.expandedOptions}>
          {expandedGroups.has("format") && (
            <>
              <button
                onClick={() => editor.chain().focus().toggleBold().run()}
                disabled={!editor.can().chain().focus().toggleBold().run()}
                className={editor.isActive("bold") ? styles.active : ""}
              >
                Bold
              </button>
              <button
                onClick={() => editor.chain().focus().toggleItalic().run()}
                disabled={!editor.can().chain().focus().toggleItalic().run()}
                className={editor.isActive("italic") ? styles.active : ""}
              >
                Italic
              </button>
              <button
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                disabled={!editor.can().chain().focus().toggleUnderline().run()}
                className={editor.isActive("underline") ? styles.active : ""}
              >
                Underline
              </button>
              <button
                onClick={() => editor.chain().focus().toggleStrike().run()}
                disabled={!editor.can().chain().focus().toggleStrike().run()}
                className={editor.isActive("strike") ? styles.active : ""}
              >
                Strike
              </button>
            </>
          )}

          {expandedGroups.has("headings") && (
            <div className={styles.expandedOptions}>
              <button
                onClick={() => toggleHeadingStyle(1)}
                className={editor.isActive("heading", { level: 1 }) ? styles.active : ""}
              >
                H1
              </button>
              <button
                onClick={() => toggleHeadingStyle(2)}
                className={editor.isActive("heading", { level: 2 }) ? styles.active : ""}
              >
                H2
              </button>
              <button
                onClick={() => toggleHeadingStyle(3)}
                className={editor.isActive("heading", { level: 3 }) ? styles.active : ""}
              >
                H3
              </button>
              <button
                onClick={() => toggleHeadingStyle(4)}
                className={editor.isActive("heading", { level: 4 }) ? styles.active : ""}
              >
                H4
              </button>
              <button
                onClick={() => toggleHeadingStyle(5)}
                className={editor.isActive("heading", { level: 5 }) ? styles.active : ""}
              >
                H5
              </button>
              <button
                onClick={() => toggleHeadingStyle(6)}
                className={editor.isActive("heading", { level: 6 }) ? styles.active : ""}
              >
                H6
              </button>
              <button
                onClick={() => editor.chain().focus().setParagraph().run()}
                className={editor.isActive("paragraph") ? styles.active : ""}
              >
                Paragraph
              </button>
            </div>
          )}

          {expandedGroups.has("lists") && (
            <>
              <button
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                className={editor.isActive("bulletList") ? styles.active : ""}
              >
                Bullet List
              </button>
              <button
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                className={editor.isActive("orderedList") ? styles.active : ""}
              >
                Numbered List
              </button>
              <button
                onClick={() => editor.chain().focus().toggleTaskList().run()}
                className={editor.isActive("taskList") ? styles.active : ""}
              >
                Task List
              </button>
            </>
          )}

          {expandedGroups.has("blocks") && (
            <>
              <button
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                className={editor.isActive("blockquote") ? styles.active : ""}
              >
                Blockquote
              </button>
              <button
                onClick={() => editor.chain().focus().setHorizontalRule().run()}
              >
                Horizontal Rule
              </button>
              <button
                onClick={() => editor.chain().focus().setCodeBlock().run()}
                className={editor.isActive("codeBlock") ? styles.active : ""}
              >
                Code Block
              </button>
            </>
          )}

          {expandedGroups.has("align") && (
            <>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
                className={
                  editor.isActive({ textAlign: "left" }) ? styles.active : ""
                }
              >
                Left
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
                className={
                  editor.isActive({ textAlign: "center" }) ? styles.active : ""
                }
              >
                Center
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
                className={
                  editor.isActive({ textAlign: "right" }) ? styles.active : ""
                }
              >
                Right
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setTextAlign("justify").run()
                }
                className={
                  editor.isActive({ textAlign: "justify" }) ? styles.active : ""
                }
              >
                Justify
              </button>
            </>
          )}

          {expandedGroups.has("table") && (
            <>
              <button
                onClick={() =>
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run()
                }
              >
                Insert Table
              </button>
              <button
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                disabled={!editor.can().addColumnBefore()}
              >
                Add Column Before
              </button>
              <button
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={!editor.can().addColumnAfter()}
              >
                Add Column After
              </button>
              <button
                onClick={() => editor.chain().focus().deleteColumn().run()}
                disabled={!editor.can().deleteColumn()}
              >
                Delete Column
              </button>
              <button
                onClick={() => editor.chain().focus().addRowBefore().run()}
                disabled={!editor.can().addRowBefore()}
              >
                Add Row Before
              </button>
              <button
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={!editor.can().addRowAfter()}
              >
                Add Row After
              </button>
              <button
                onClick={() => editor.chain().focus().deleteRow().run()}
                disabled={!editor.can().deleteRow()}
              >
                Delete Row
              </button>
              <button
                onClick={() => editor.chain().focus().deleteTable().run()}
                disabled={!editor.can().deleteTable()}
              >
                Delete Table
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().toggleHeaderColumn().run()
                }
                disabled={!editor.can().toggleHeaderColumn()}
              >
                Toggle Header Column
              </button>
              <button
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                disabled={!editor.can().toggleHeaderRow()}
              >
                Toggle Header Row
              </button>
              <button
                onClick={() => editor.chain().focus().toggleHeaderCell().run()}
                disabled={!editor.can().toggleHeaderCell()}
              >
                Toggle Header Cell
              </button>
              <button
                onClick={() => editor.chain().focus().mergeCells().run()}
                disabled={!editor.can().mergeCells()}
              >
                Merge Cells
              </button>
              <button
                onClick={() => editor.chain().focus().splitCell().run()}
                disabled={!editor.can().splitCell()}
              >
                Split Cell
              </button>
            </>
          )}

          {expandedGroups.has("colors") && (
            <>
              <button
                onClick={() => editor.chain().focus().setColor("#07587f").run()}
                className={`${styles.colorButton} blue ${
                  editor.isActive("textStyle", { color: "#07587f" }) ? styles.active : ""
                }`}
              >
                Blue
              </button>
              <button
                onClick={() => editor.chain().focus().setColor("#09293B").run()}
                className={`${styles.colorButton} dark ${
                  editor.isActive("textStyle", { color: "#09293B" }) ? styles.active : ""
                }`}
              >
                Dark
              </button>
              <button
                onClick={() =>
                  editor.chain().focus().setColor("rgba(7, 88, 127, 0.1)").run()
                }
                className={`${styles.colorButton} light ${
                  editor.isActive("textStyle", { color: "rgba(7, 88, 127, 0.1)" })
                    ? styles.active
                    : ""
                }`}
              >
                Light
              </button>
              <button
                onClick={() => editor.chain().focus().setColor("#FFFFFF").run()}
                className={`${styles.colorButton} white ${
                  editor.isActive("textStyle", { color: "#FFFFFF" }) ? styles.active : ""
                }`}
              >
                White
              </button>
            </>
          )}

          {expandedGroups.has("media") && (
            <>
              <ImageUpload />
              <ImageGroup />
              {/* <button
                onClick={() => {
                  const url = window.prompt("Enter image URL");
                  if (url) {
                    editor.chain().focus().setImage({ src: url }).run();
                  }
                }}
              >
                Insert Image URL
              </button> */}
              <button
                onClick={() => editor.chain().focus().deleteSelection().run()}
                disabled={!editor.isActive("image")}
              >
                Remove Image
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MenuBar;
